import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

/**
 * The visitor's side of phone verification.
 *
 * Holds one rule that matters more than the rest: changing the number throws
 * the proof away. A token is bound to the number it verified, so a visitor who
 * verifies 98765 and then edits the last digit is no longer verified - and the
 * server would refuse the submission anyway. Clearing it here means they find
 * out at the field instead of at the submit button.
 */
export function usePhoneVerification({ formConfig, phone }) {
  const [channels, setChannels] = useState([]);
  const [required, setRequired] = useState(false);
  const [status, setStatus] = useState('idle');   // idle|sending|sent|verifying|verified
  const [challengeId, setChallengeId] = useState(null);
  const [channel, setChannel] = useState(null);
  const [masked, setMasked] = useState('');
  const [token, setToken] = useState(null);
  const [verifiedPhone, setVerifiedPhone] = useState(null);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  // Which number the current cooldown belongs to. The wait is per NUMBER on the
  // server, so holding a client-side timer over a corrected typo made us
  // stricter than the rule we were enforcing - someone who mistyped a digit had
  // to sit out 45 seconds before they could try the right number.
  const [cooldownFor, setCooldownFor] = useState(null);
  // Which number the OPEN challenge belongs to. Without this, correcting a
  // mistyped number left the panel saying "Code sent to <the new number>" when
  // the code had gone to the old one - the screen asserting something untrue
  // about a message the visitor is sitting there waiting for.
  const [challengeFor, setChallengeFor] = useState(null);
  const timer = useRef(null);

  const enabled = Boolean(formConfig?.verify_phone);

  // What this workspace can actually send. Asked once, as the form loads, so
  // the Verify button never appears on a workspace with no configured channel -
  // an offer we cannot honour is worse than no offer.
  useEffect(() => {
    if (!enabled || !formConfig?.id) return;
    let dead = false;
    axios.get(`${API_URL}/api/otp/channels`, { params: { form_id: formConfig.id } })
      .then(({ data }) => {
        if (dead) return;
        setChannels(data.channels || []);
        setRequired(Boolean(data.required));
      })
      .catch(() => { /* leave channels empty - the gate simply does not render */ });
    return () => { dead = true; };
  }, [enabled, formConfig?.id]);

  // Countdown for the resend link.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    timer.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer.current);
  }, [cooldown]);

  // Editing the number invalidates everything proved about the old one, and
  // releases a cooldown that was earned by a different number.
  useEffect(() => {
    const now = String(phone || '').replace(/\D/g, '');
    if (verifiedPhone && now !== String(verifiedPhone).replace(/\D/g, '')) {
      setToken(null); setVerifiedPhone(null); setStatus('idle');
      setChallengeId(null); setError('');
    }
    // A code already in flight belongs to the number it was sent to. Changing
    // the number abandons it rather than pretending it followed along.
    if (challengeFor && now !== challengeFor) {
      setStatus('idle'); setChallengeId(null); setChallengeFor(null); setError('');
    }
    if (cooldownFor && now !== cooldownFor) { setCooldown(0); setCooldownFor(null); }
  }, [phone, verifiedPhone, cooldownFor, challengeFor]);

  const send = useCallback(async (which) => {
    setError(''); setStatus('sending');
    try {
      const { data } = await axios.post(`${API_URL}/api/otp/start`, {
        form_id: formConfig?.id, phone, channel: which || channels[0],
      });
      setChallengeId(data.challenge_id);
      setChannel(data.channel);
      setMasked(data.masked_phone);
      setCooldown(data.resend_in || 45);
      setCooldownFor(String(phone || '').replace(/\D/g, ''));
      setChallengeFor(String(phone || '').replace(/\D/g, ''));
      setStatus('sent');
      return true;
    } catch (e) {
      const d = e.response?.data || {};
      setError(d.error || 'Could not send a code. Please try again.');
      if (d.retry_after) { setCooldown(d.retry_after); setCooldownFor(String(phone || '').replace(/\D/g, '')); }
      // Back to idle, not stuck on 'sending' - a failed send must leave the
      // button pressable again.
      setStatus(challengeId ? 'sent' : 'idle');
      return false;
    }
  }, [formConfig?.id, phone, channels, challengeId]);

  const verify = useCallback(async (code) => {
    setError(''); setStatus('verifying');
    try {
      const { data } = await axios.post(`${API_URL}/api/otp/check`, {
        form_id: formConfig?.id, challenge_id: challengeId, code,
      });
      setToken(data.token);
      setVerifiedPhone(phone);
      setStatus('verified');
      return true;
    } catch (e) {
      setError(e.response?.data?.error || 'That code is not right.');
      setStatus('sent');
      return false;
    }
  }, [formConfig?.id, challengeId, phone]);

  return {
    // `active` is the only thing the form should branch on: verification is on
    // for this form AND this workspace can actually deliver a code.
    active: enabled && channels.length > 0,
    required, channels, status, channel, masked, token, error, cooldown,
    verified: status === 'verified',
    send, verify,
    clearError: () => setError(''),
  };
}
